import React, { useState } from "react";
import { useColorMode } from "theme-ui";

import Icons from "@icons";
import Logo from "@components/Logo";
import Section from "@components/Section";
import Headings from "@components/Headings";

import styled from "@emotion/styled";
import mediaqueries from "@styles/media";

const validEmail = (email) => {
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

const encode = (data) => {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
};

interface Props {
  hideLogo?: boolean;
}

const Subscription = ({ hideLogo }: Props) => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const [colorMode] = useColorMode();
  const fill = colorMode === "dark" ? "#fff" : "#000";

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.currentTarget.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const form = e.target;

    if (!validEmail(email)) {
      setEmail("");
      setLoading(false);
      return setEmailError("Please enter a valid email address!");
    }

    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": form.getAttribute("name"),
          email,
        }),
      });

      setLoading(false);
      setSubscribed(true);
    } catch {
      setEmail("");
      setLoading(false);
      setEmailError(
        "Uh oh! Something went wrong 😕. Make sure you enter a valid email and/or try again later."
      );
    }
  };

  const subscribeText = subscribed ? (
    <Heading>You've subscribed 🎉!</Heading>
  ) : (
    <Text>
      Subscribe to the{" "}
      <a href="https://www.frontendfresh.com/" target="_blank">
        Front-end Fresh Newsletter
      </a>{" "}
      to receive front-end engineering tips, tutorials, and projects on a weekly
      basis.
    </Text>
  );

  const errorMessage = emailError && !subscribed && <Error>{emailError}</Error>;
  const inputElement = subscribed ? null : (
    <>
      <Icons.Mailto />
      <label htmlFor="email" hidden>
        Email
      </label>
      <Input
        placeholder="your@email.com"
        name="email"
        type="email"
        id="email"
        value={email}
        disabled={loading}
        onChange={handleEmailChange}
      />
    </>
  );

  let subscribeMessage;

  if (loading) {
    subscribeMessage = "Loading...";
  } else if (subscribed) {
    subscribeMessage = <CheckMarkIcon />;
  } else {
    subscribeMessage = "Subscribe";
  }

  const logo = hideLogo ? null : <Logo fill={fill} />;

  return (
    <Section narrow>
      <SubscriptionContainer>
        {logo}
        <Content>
          {subscribeText}
          <Form
            autoComplete="off"
            name="contact"
            method="post"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            action="/thanks/"
            onSubmit={handleSubmit}
          >
            {/* The `form-name` hidden field is required to support form submissions without JavaScript */}
            <input type="hidden" name="form-name" value="contact" />
            <p hidden>
              <label>
                Don’t fill this out:{" "}
                <input name="bot-field" onChange={handleEmailChange} />
              </label>
            </p>
            {inputElement}
            <Button type="submit" subscribed={subscribed} disabled={loading}>
              {subscribeMessage}
            </Button>
            {errorMessage}
          </Form>
        </Content>
      </SubscriptionContainer>
    </Section>
  );
};

export default Subscription;

const SubscriptionContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  padding: 60px 0 50px;
  margin: 10px auto 100px;
  background: ${(p) => p.theme.colors.card};
  box-shadow: 0px 4px 50px rgba(0, 0, 0, 0.05);
  z-index: 1;

  text-align: center;

  ${mediaqueries.tablet`
    padding: 50px 0 0;
    text-align: center;
  `}
  ${mediaqueries.phablet`
    margin: -20px auto 80px;
  `}
`;

const Content = styled.div`
  margin: 0 auto;
  width: 100%;
  max-width: 640px;
  margin-top: 30px;
  text-align: center;
  ${mediaqueries.tablet`
    h3 {
      padding: 0 50px;
    }
  `}
  ${mediaqueries.phone`
    h3 {
      padding: 0 24px;
    }
  `}
`;

const Heading = styled(Headings.h3)`
  margin-bottom: 20px;
  ${mediaqueries.tablet`
    margin-bottom: 15px;
  `}
`;

const Text = styled.p`
  margin: 0px auto 30px;
  color: ${(p) => p.theme.colors.primary};
  line-height: 1.75;
  a {
    color: ${(p) => p.theme.colors.accent};
    font-weight: bold;
  }
  ${mediaqueries.tablet`
    padding: 0 26px;
    margin: 0 auto 25px;
  `}
`;

const Form = styled.form<{ hasError?: boolean }>`
  position: relative;

  svg {
    * {
      fill: ${(p) => p.theme.colors.accent};
    }
  }

  svg {
    position: absolute;
    top: 16px;
    left: 97px;
    ${mediaqueries.tablet`
      top: 17px; 
      left: 28px;
    `}
  }
`;

const Input = styled.input<{ hasError?: boolean }>`
  position: relative;
  background: ${(p) =>
    p.hasError
      ? p.theme.colors.errorBackground
      : p.theme.colors.inputBackground};
  border-radius: 35px;
  border: none;
  padding: 13px 21px 13px 35px;
  margin-bottom: 30px;
  width: 471px;
  color: ${(p) => p.theme.colors.primary};
  ::placeholder {
    color: ${(p) => p.theme.colors.track};
    opacity: 1;
  }
  :-ms-input-placeholder {
    color: ${(p) => p.theme.colors.track};
  }
  ::-ms-input-placeholder {
    color: ${(p) => p.theme.colors.track};
  }
  ${mediaqueries.tablet`
    width: calc(100% - 36px);
    margin: 0 18px;
    padding: 14px 14px 14px 30px;
    margin-bottom: 30px;
  `}
`;

const Button = styled.button<{ hasError?: boolean; subscribed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 161px;
  height: 38px;
  margin: 0 auto;
  border: 1px solid ${(p) => p.theme.colors.accent};
  color: ${(p) => p.theme.colors.background};
  background: ${(p) => p.theme.colors.accent};
  font-weight: 600;
  border-radius: 35px;
  letter-spacing: 0.42px;
  transition: border-color 0.2s var(--ease-in-out-quad),
    background 0.2s var(--ease-in-out-quad), color 0.2s var(--ease-in-out-quad);
  pointer-events: ${(p) => (p.subscribed ? "none" : "initial")};
  &[disabled] {
    cursor: not-allowed;
  }
  svg {
    position: initial;
    * {
      fill: ${(p) => p.subscribed && p.theme.colors.background};
    }
  }
  ${(p) => mediaqueries.tablet`
    position: relative;
    height: 60px;
    width: 100%;
    top: 0;
    left: 0;
    border: none;
    border-radius: 0;
    border-top: 1px solid ${p.theme.colors.horizontalRule};
  `}
`;

const Error = styled.div`
  padding-top: 10px;
  color: ${(p) => p.theme.colors.error};
  font-size: 13px;
  a {
    color: ${(p) => p.theme.colors.error};
    text-decoration: underline;
  }
  ${mediaqueries.tablet`
    left: 50px;
    top: 50px;
  `}
`;

const CheckMarkIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.00016 16.1698L4.83016 11.9998L3.41016 13.4098L9.00016 18.9998L21.0002 6.99984L19.5902 5.58984L9.00016 16.1698Z"
      fill="#08080B"
    />
  </svg>
);
